import React, { useEffect, useState } from 'react';
import SendIcon from '@mui/icons-material/Send';
import _ from 'lodash';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Grid, Stack, Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import PushPreviewer from '../previewer/PushPreviewer';
import DashboardLayout from '../../../../layouts/Dashboard';
import { formatIsoTimestamp, getPushActionDestinationLabel, getPushActionLabel, getPushStatusChip } from '../../../../utils/mappers';
import { mobilizeiPushNotificationsService } from '../../../../services/mobilizei/pushNotifications.service';
import { useUser } from '../../../../contexts/user.context';
import { showApiError } from '../../../../utils/errors';

export default function PushDetail() {
    const { user } = useUser();
    const navigate = useNavigate();
    const { id } = useParams();

    const platformId = user?.store?.platformId;
    const storeId = user?.store?.id;

    const [isLoading, setIsLoading] = useState(true);
    const [notification, setNotification] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        getNotification();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const getNotification = () => {
        if (!platformId || !storeId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        mobilizeiPushNotificationsService.getPushNotificationById(platformId, storeId, id)
            .then((response) => {
                setNotification(response);
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const handleConfirm = () => {
        if (!confirmAction) return;

        const { type } = confirmAction;

        const request = type === 'cancel'
            ? mobilizeiPushNotificationsService.cancelPushNotification(platformId, storeId, id)
            : mobilizeiPushNotificationsService.deletePushNotification(platformId, storeId, id);

        setIsProcessing(true);

        request
            .then(() => {
                toast.success(type === 'cancel'
                    ? 'Notificação cancelada com sucesso!'
                    : 'Notificação excluída com sucesso!');
                setConfirmAction(null);

                if (type === 'delete') {
                    navigate('/push');
                } else {
                    getNotification();
                }
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsProcessing(false);
            });
    }

    const renderRow = (label, value) => (
        <Box display="flex" alignItems="flex-start" py={1.5} sx={{ borderBottom: '1px solid #ededed' }}>
            <Typography variant="body2" color="textSecondary" sx={{ width: 200, flexShrink: 0 }}>
                {label}
            </Typography>
            <Box flex={1} sx={{ wordBreak: 'break-word' }}>
                {(typeof value === 'string' || typeof value === 'number')
                    ? <Typography variant="body2">{value}</Typography>
                    : value}
            </Box>
        </Box>
    );

    const renderSection = (title, children) => (
        <Box mb={4}>
            <Typography variant="h6" sx={{ fontWeight: 700, pb: 1, mb: 1, borderBottom: '2px solid #000' }}>
                {title}
            </Typography>
            {children}
        </Box>
    );

    const canCancel = notification?.status === 'scheduled';
    const canDelete = ['sent', 'canceled', 'failed'].includes(notification?.status);

    return (
        <DashboardLayout
            Icon={SendIcon}
            title="Detalhes da notificação"
            subtitle="Visualize o conteúdo, a data de envio e o status da notificação"
        >
            {isLoading ? (
                <Box display="flex" alignItems="center" justifyContent="center" mt={2} mb={6}>
                    <CircularProgress />
                </Box>
            ) : _.isEmpty(notification) ? (
                <Box textAlign="center" mt={2} mb={6}>
                    <Typography variant="body1">Notificação não encontrada.</Typography>
                    <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/push')}>
                        Voltar para a lista
                    </Button>
                </Box>
            ) : (
                <Grid container spacing={4} mt={1}>
                    <Grid item md={7} xs={12}>
                        {renderSection('Detalhes', (
                            <>
                                {renderRow('Status', getPushStatusChip(notification.status))}
                                {renderRow('Título', notification.title || '-')}
                                {renderRow('Mensagem', notification.text || '-')}
                                {renderRow('Ação', getPushActionLabel(notification.action))}
                                {notification.action === 'goToProduct' || notification.action === 'goToCategory' ? (
                                    !!notification.linkName && renderRow(getPushActionDestinationLabel(notification.action), notification.linkName)
                                ) : (
                                    !!notification.link && renderRow(getPushActionDestinationLabel(notification.action), notification.link)
                                )
                                }
                            </>
                        ))}

                        {renderSection('Agendamento', (
                            <>
                                {renderRow('Tipo de envio', notification.sendType === 'scheduled' ? 'Agendado' : 'Imediato')}
                                {notification.sendType === 'scheduled' &&
                                    renderRow('Agendada para', formatIsoTimestamp(notification.scheduledFor, 'DD/MM/YYYY HH:mm') || '-')}
                                {notification.status === 'sent' &&
                                    renderRow('Enviada em', formatIsoTimestamp(notification.sentAt, 'DD/MM/YYYY HH:mm') || '-')}
                                {renderRow('Criada em', formatIsoTimestamp(notification.createdAt, 'DD/MM/YYYY HH:mm') || '-')}
                            </>
                        ))}

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Button variant="outlined" onClick={() => navigate('/push')}>
                                Voltar
                            </Button>
                            {canCancel && (
                                <Button variant="outlined" color="error" onClick={() => setConfirmAction({ type: 'cancel' })}>
                                    Cancelar
                                </Button>
                            )}
                            {canDelete && (
                                <Button variant="outlined" color="error" onClick={() => setConfirmAction({ type: 'delete' })}>
                                    Excluir
                                </Button>
                            )}
                        </Stack>
                    </Grid>

                    <Grid item md={5} xs={12}>
                        <PushPreviewer payload={notification} />
                    </Grid>
                </Grid>
            )}

            <Dialog open={!!confirmAction} onClose={() => !isProcessing && setConfirmAction(null)}>
                <DialogTitle>
                    {confirmAction?.type === 'cancel' ? 'Cancelar notificação' : 'Excluir notificação'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmAction?.type === 'cancel'
                            ? 'Tem certeza que deseja cancelar o envio desta notificação agendada?'
                            : 'Ao excluir esta notificação, ela será removida permanentemente do histórico. Deseja continuar?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAction(null)} disabled={isProcessing} variant='outlined'>
                        Voltar
                    </Button>
                    {isProcessing ? (
                        <Box display="flex" alignItems="center" px={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Button onClick={handleConfirm} variant="outlined" color="error">
                            {confirmAction?.type === 'cancel' ? 'Cancelar envio' : 'Excluir'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}
